import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { StyleContext } from "../../providers/StyleContextProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { AuthContext } from "../../providers/AuthContextProvider";
import { Footer } from "../../components/shared/Footer";
import { Button } from "../../components/shared/Button";
import { deleteTuitionAd, getTuitionAdDetails, getAdminAdSearchOptions, getTuitionAdsCount, getAdSearchResults } from "../../services/TuitionService";
import { TuitionAdsCount } from "../../models/AdsCount";
import { TuitionAdSearchCriteria } from "../../models/TuitionAdSearchCriteria";
import { Select } from "../../components/shared/Select";
import { Option } from "../../models/Option";
import { TuitionAd } from "../../models/TuitionAd";
import { TuitionAdSearchOptions } from "../../models/TuitionAdSearchOptions";
import { Pagination } from "../../components/shared/Pagination";
import { ReactComponent as Delete } from "../../assets/svgs/bin.svg";
import { ReactComponent as Edit } from "../../assets/svgs/edit.svg";
import { ReactComponent as View } from "../../assets/svgs/eye-open.svg";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { DetailLoader } from "../../components/loaders/DetailLoader";
import { NoData } from "../../components/shared/NoData";
import { Header } from "../../components/shared/Header";
import { TableLoader } from "../../components/loaders/TableLoader";
import { SelectLoader } from "../../components/loaders/SelectLoader";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { getLanguageCode } from "../../helpers/Common";

export const AdListPage: React.FC = () => {
    const [searchParams] = useSearchParams(); 
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("teachersPage"));  
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const [adsCount, setAdsCount] = useState<TuitionAdsCount>(); 
    const [status, setStatus] = useState<string>("LIVE");
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const [totalPages, setTotalPages] = useState<number>();       
    const pageSize: number = 40;
    const [teachers, setTeachers] = useState<Option[]>([]);
    const teacherId: string | undefined = searchParams.get("teacherId") === null ? undefined : searchParams.get("teacherId")!;
    const [subjects, setSubjects] = useState<Option[]>([]);
    const subjectId: string | undefined = searchParams.get("subjectId") === null ? undefined : searchParams.get("subjectId")!;
    const [classTypes, setClassTypes] = useState<Option[]>([]);
    const classTypeId: string | undefined = searchParams.get("classTypeId") === null ? undefined : searchParams.get("classTypeId")!;
    const [districts, setDistricts] = useState<Option[]>([]);
    const districtId: string | undefined = searchParams.get("districtId") === null ? undefined : searchParams.get("districtId")!;
    const [optionLoading, setOptionLoading] = useState<boolean>(true);  
    const [teacherAds, setTeacherAds] = useState<TuitionAd[]>();
    const [adLoading, setAdLoading] = useState<boolean>(true);    
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [adViewLoading, setAdViewLoading] = useState<boolean>(true);
    const [openViewDialog, setOpenViewDialog] = useState<boolean>(false);
    const [adView, setAdView] = useState<TuitionAd>();  
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false); 
    const [adDelete, setAdDelete] = useState<TuitionAd>();
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getAdsCount();
            getSearchOptions();
            window.scrollTo(0, 0);
        }
    }, []);

    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("teachersPage")); 
    }, [isMobile]);

    useEffect(() => {
        getTuitionAdList();
        window.scrollTo(0, 0);                                     
    }, [page, status, teacherId, subjectId, classTypeId, districtId]);

    async function getAdsCount() {                
        await getTuitionAdsCount()
        .then((tuitionAdsCount: TuitionAdsCount) => {            
            setAdsCount(tuitionAdsCount);                   
        })
        .catch((error) => {
            toErrorPage(error);
        });                            
    }

    async function getSearchOptions() {
        await getAdminAdSearchOptions()
        .then((searchOptions: TuitionAdSearchOptions) => {  
            setTeachers(searchOptions.teachers);                      
            setSubjects(searchOptions.subjects);            
            setClassTypes(searchOptions.classTypes);
            setDistricts(searchOptions.districts);                       
            setOptionLoading(false);            
        })
        .catch((error) => {
            toErrorPage(error);
        });
    }

    async function getTuitionAdList() {        
        setAdLoading(true);
        let searchModel: TuitionAdSearchCriteria = {
            PageNo: page,
            RecordsPerPage: pageSize,
            Status: status,
	        TeacherId: teacherId ? Number(teacherId) : undefined,
            SubjectId: subjectId ? Number(subjectId) : undefined,
            ClassTypeId: classTypeId ? Number(classTypeId) : undefined,
            DistrictId: districtId ? Number(districtId) : undefined,
            LanguageCode: getLanguageCode()
        }
        await getAdSearchResults(searchModel)
        .then((tuitionAdList: TuitionAd[]) => {
            setTeacherAds(tuitionAdList);            
            if(tuitionAdList.length > 0) { 
                let totalPageCount = Math.ceil(tuitionAdList[0].recordCount / pageSize);            
                setTotalPages(totalPageCount);                
            }  
            else {
                setTotalPages(undefined);
            }  
            setAdLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const navigateToNewAds = () => {
        navigate('/tuitions/new-ads');
    }

    async function openAdView(adId: number) {
        setOpenViewDialog(true);
        setAdViewLoading(true);
        await getTuitionAdDetails(adId)
        .then((tuitionAdView: TuitionAd) => {
            setAdView(tuitionAdView);               
            setAdViewLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    const openDeleteView = (ad: TuitionAd) => {
        setAdDelete(ad);
        setOpenDeleteDialog(true);
    }

    async function deleteAd() {
        await deleteTuitionAd(adDelete!.tuitionAdId)
        .then((success: boolean) => {
            setOpenDeleteDialog(false);
            getAdsCount();
            getTuitionAdList();
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    const navigateToEditAds = (adId: number) => {
        navigate('/tuitions/edit-ads/' + adId);
    }

    const handlePagination = (pageNumber: number) => {
        searchParams.set("page", pageNumber.toString());                     
        const newUrl = `?${searchParams}`;
        navigate(newUrl);        
    }

    const setTeacherId = (selectedTeacherId: string) => {
        if(selectedTeacherId) {
            searchParams.set("page", "1");
            searchParams.set("teacherId", selectedTeacherId);
        }
        else {
            searchParams.delete("teacherId");
        }
        const newUrl = `?${searchParams}`;
        navigate(newUrl);        
    }

    const setSubjectId = (selectedSubjectId: string) => {        
        if(selectedSubjectId) {
            searchParams.set("page", "1");
            searchParams.set("subjectId", selectedSubjectId);
        }
        else {
            searchParams.delete("subjectId");
        }                   
        const newUrl = `?${searchParams}`;
        navigate(newUrl);        
    }

    const setClassTypeId = (selectedClassTypeId: string) => {        
        if(selectedClassTypeId) {
            searchParams.set("page", "1");
            searchParams.set("classTypeId", selectedClassTypeId);
        }
        else {
            searchParams.delete("classTypeId");
        }                     
        const newUrl = `?${searchParams}`;
        navigate(newUrl);        
    }

    const setDistrictId = (selectedDistrictId: string) => {        
        if(selectedDistrictId) {
            searchParams.set("page", "1");
            searchParams.set("districtId", selectedDistrictId);
        }
        else {
            searchParams.delete("districtId");
        }                     
        const newUrl = `?${searchParams}`;
        navigate(newUrl);        
    }

    return (   
        <>
            <div style={styles}>
                <Header />
                <h1 style={styles.heading}>{"Tuition Teachers"}</h1>
                {adsCount && <div style={styles.buttonsWrapper}>
                    <Button 
                        style={{...styles.statusButton, ...styles.statusButton.all, ...(status === 'ALL' && styles.statusButton.active)}}
                        onClick={() => setStatus('ALL')}
                    >
                        {"All " + adsCount.allCount}
                    </Button>
                    <Button 
                        style={{...styles.statusButton, ...styles.statusButton.live, ...(status === 'LIVE' && styles.statusButton.active)}}
                        onClick={() => setStatus('LIVE')}
                    >
                        {"Live " + adsCount.liveCount}
                    </Button>
                    <Button 
                        style={{...styles.statusButton, ...styles.statusButton.pending, ...(status === 'PENDING' && styles.statusButton.active)}}
                        onClick={() => setStatus('PENDING')}
                    >
                        {"Pending " + adsCount.pendingCount}
                    </Button>
                    <Button 
                        style={{...styles.statusButton, ...styles.statusButton.unpaid, ...(status === 'UNPAID' && styles.statusButton.active)}}
                        onClick={() => setStatus('UNPAID')}
                    >
                        {"Unpaid " + adsCount.unpaidCount}
                    </Button>
                    <Button 
                        style={{...styles.statusButton, ...styles.statusButton.expired, ...(status === 'EXPIRED' && styles.statusButton.active)}}
                        onClick={() => setStatus('EXPIRED')}
                    >
                        {"Expired " + adsCount.expiredCount}
                    </Button>
                    <Button style={styles.newAdButton} onClick={navigateToNewAds}>{"New Ads"}</Button>
                </div>}                
                {optionLoading ? <SelectLoader count={4} />  : 
                <div style={styles.searchWrapper}>
                    <Select 
                        styles={styles.searchSelect}
                        label={"Teachers"} 
                        options={teachers} 
                        initialValue={teacherId}
                        getSelectedValue={setTeacherId} 
                    />
                    <Select 
                        styles={styles.searchSelect}
                        label={"Subjects"} 
                        options={subjects} 
                        initialValue={subjectId}
                        getSelectedValue={setSubjectId} 
                    />
                    <Select 
                        styles={styles.searchSelect}
                        label={"Class Types"} 
                        options={classTypes} 
                        initialValue={classTypeId}
                        getSelectedValue={setClassTypeId} 
                    />
                    <Select 
                        styles={styles.searchSelect}
                        label={"Districts"} 
                        options={districts} 
                        initialValue={districtId}
                        getSelectedValue={setDistrictId} 
                    />
                </div>}
                {adLoading ? <TableLoader columnCount={8} rowCount={9} /> : 
                <>
                    {teacherAds && <>                
                        {(teacherAds.length === 0) ? <NoData/> :
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.table.header}> 
                                    <th style={styles.table.idColumn}>{"Id"}</th>                           
                                    <th style={styles.table.imageColumn}>{"Image"}</th>
                                    <th style={styles.table.nameColumn}>{"Name"}</th> 
                                    <th style={styles.table.subjectColumn}>{"Subjects"}</th>
                                    <th style={styles.table.typeColumn}>{"Class Types"}</th>
                                    <th style={styles.table.districtColumn}>{"Districts"}</th>
                                    <th style={styles.table.datesColumn}>{"Dates"}</th>
                                    <th style={styles.table.actionColumn}>{"Action"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teacherAds.map((tuitionAd, rowIndex) => (
                                    <tr key={rowIndex} style={styles.table.tr}>
                                        <td>{tuitionAd.tuitionAdId}</td>
                                        <td><img style={styles.table.image} src={tuitionAd.tuitionAdUrl} /></td>
                                        <td>
                                            {tuitionAd.teacherName}<br/>
                                            {tuitionAd.whatsappNo}
                                        </td>
                                        <td>{tuitionAd.subjects}</td>
                                        <td>{tuitionAd.classTypes}</td>
                                        <td>{tuitionAd.districts}</td>
                                        <td>
                                            <div>Start Date: {tuitionAd.createdDate!.split("T")[0]}</div>
                                            <div>Expiry Date: {tuitionAd.expiryDate ? tuitionAd.expiryDate.split("T")[0] : "Not Set"}</div>
                                        </td>
                                        <td style={styles.table.actionCell}>
                                            <div style={styles.table.iconWrapper}>
                                                <span style={styles.table.action} onClick={() => openAdView(tuitionAd.tuitionAdId)}><View style={{...styles.table.icon, ...styles.table.icon.view}}/>{"View"}</span>
                                                <span style={styles.table.action} onClick={() => navigateToEditAds(tuitionAd.tuitionAdId)}><Edit style={{...styles.table.icon, ...styles.table.icon.edit}}/>{"Edit"}</span>
                                                <span style={styles.table.action} onClick={() => openDeleteView(tuitionAd)}><Delete style={{...styles.table.icon, ...styles.table.icon.delete}}/>{"Delete"}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>}
                        {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination}/>}
                    </>}
                </>}
            </div>      
            <PopUpDialog styles={styles.viewDialog} open={openViewDialog} toggle={() => setOpenViewDialog(!openViewDialog)}>
                <>                
                    {adViewLoading ? <DetailLoader/> :                                
                    <div style={styles.adView.container}>                        
                        <div style={styles.adView.detailContainer}>
                            <div style={styles.adView.id}>{"Teacher Ad Id: " + adView?.tuitionAdId}</div>
                            <div style={styles.name}>
                                {adView?.teacherName}                                    
                            </div> 
                            <div style={styles.tepNo}>
                                {adView?.whatsappNo}                                                          
                            </div>
                            <div>
                                <div style={styles.adView.label}>{'Subjects'}</div>
                                <div style={styles.adView.value}>{adView?.subjects}</div>
                            </div>                    
                            <div>
                                <div style={styles.adView.label}>{'Grades'}</div>                            
                                <div style={styles.adView.value}>{adView?.grades ? adView?.grades : "-"}</div>
                            </div>
                            <div>
                                <div style={styles.adView.label}>{'Mediums'}</div>                            
                                <div style={styles.adView.value}>{adView?.mediums ? adView?.mediums : "-"}</div>
                            </div>
                            <div>
                                <div style={styles.adView.label}>{'Class Types'}</div>                            
                                <div style={styles.adView.value}>{adView?.classTypes ? adView?.classTypes : "-"}</div>
                            </div>
                            <div>
                                <div style={styles.adView.label}>{'Districts'}</div>                            
                                <div style={styles.adView.value}>{adView?.districts ? adView?.districts : "-"}</div>
                            </div>
                            <div>
                                <div style={styles.adView.label}>{'Towns'}</div>                            
                                <div style={styles.adView.value}>{adView?.towns ? adView?.towns : "-"}</div>
                            </div>
                        </div>
                        <img src={adView?.tuitionAdUrl} style={styles.adView.image}></img> 
                    </div>}
                </>
            </PopUpDialog>
            <PopUpDialog styles={styles.deleteDialog} open={openDeleteDialog} toggle={() => setOpenDeleteDialog(!openDeleteDialog)}>
                <div style={styles.deleteView.text}>{adDelete?.teacherName + " ad will permanently delete."}</div>
                <Button style={styles.deleteView.button} type="button" onClick={deleteAd}>{"Delete"}</Button> 
            </PopUpDialog>
        </>         
    )
}